/**
 * Dashboard Service
 * 
 * Service สำหรับดึงข้อมูล Dashboard
 * เชื่อมกับ API จริง (DepositWithdraw, _User)
 */

import { queryClass } from '@/services/adapters/parse/parse-client'
import {
  DashboardData,
  FinancialStats,
  MemberStats,
  DailyTableData,
  ChartData,
  DashboardQueryParams,
} from './dashboard.types'

// ============================================
// Helper Functions
// ============================================

function _formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}
void _formatDate // Suppress unused warning - kept for future use

function getDateRange(type: 'today' | 'yesterday' | '7days' | '30days'): { startDate: Date; endDate: Date } {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)

  switch (type) {
    case 'today':
      return { startDate: startOfToday, endDate: endOfToday }
    case 'yesterday':
      const yesterday = new Date(startOfToday)
      yesterday.setDate(yesterday.getDate() - 1)
      const endYesterday = new Date(yesterday)
      endYesterday.setHours(23, 59, 59)
      return { startDate: yesterday, endDate: endYesterday }
    case '7days':
      const start7 = new Date(startOfToday)
      start7.setDate(start7.getDate() - 6)
      return { startDate: start7, endDate: endOfToday }
    case '30days':
      const start30 = new Date(startOfToday)
      start30.setDate(start30.getDate() - 29)
      return { startDate: start30, endDate: endOfToday }
    default:
      return { startDate: startOfToday, endDate: endOfToday }
  }
}

// ============================================
// Real Data: Calculate Financial Stats
// ============================================

/**
 * คำนวณ Financial Stats ตาม Angular calculateFinancialStats()
 * สูตร: กำไรสุทธิ = ฝากเงินสด - (ถอนเงินสด + โบนัสฝาก + คืนยอดเสีย)
 */
function calculateFinancialStats(transactions: any[]): FinancialStats {
  let cashDepositTotal = 0
  let cashWithdrawTotal = 0
  let bonusDepositTotal = 0
  let returnToMemberTotal = 0
  
  const stats: FinancialStats = {
    totalDepositAmount: 0,
    totalDepositRegularAmount: 0,
    totalDepositBonusAmount: 0,
    totalWithdrawAmount: 0,
    totalWithdrawRegularAmount: 0,
    totalWithdrawBonusAmount: 0,
    totalReturnToMemberAmount: 0,
    netProfit: 0,
    adminDepositAmount: 0,
    autoDepositAmount: 0,
    adminWithdrawAmount: 0,
    autoWithdrawAmount: 0,
    totalTransactions: transactions.length,
    depositTransactions: 0,
    withdrawTransactions: 0,
    pendingTransactions: 0,
    approvedTransactions: transactions.length, // ทุก record ที่ query มาเป็น Approved
  }

  transactions.forEach((item) => {
    const amount = parseFloat(item.amount) || 0
    const type = item.type
    const isBonus = item.isBonus === true
    const returnToMember = item.return_to_member === true
    const withdrawBy = item.withdrawBy
    const createFrom = item.create_from

    // 1. คืนยอดเสีย: return_to_member=true, type=Deposit, isBonus=false
    if (returnToMember && type === 'Deposit' && !isBonus) {
      stats.totalReturnToMemberAmount += amount
      returnToMemberTotal += amount
    }
    // 2. ฝากเงินสดปกติ: type=Deposit, isBonus=false, return_to_member!=true
    else if (type === 'Deposit' && !isBonus) {
      stats.depositTransactions++
      stats.totalDepositAmount += amount
      stats.totalDepositRegularAmount += amount
      cashDepositTotal += amount
      
      // ฝากโดยแอดมิน: create_from=Panel
      if (createFrom === 'Panel' && !returnToMember) {
        stats.adminDepositAmount += amount
      } 
      // ฝากอัตโนมัติ: create_from=Member
      else if (createFrom === 'Member' && !returnToMember) {
        stats.autoDepositAmount += amount
      }
    }
    // 3. ฝากโบนัส: type=Deposit, isBonus=true
    else if (type === 'Deposit' && isBonus) {
      stats.depositTransactions++
      stats.totalDepositAmount += amount
      stats.totalDepositBonusAmount += amount
      bonusDepositTotal += amount
    }
    // 4. ถอนเงินสด: type=Withdraw, isBonus=false
    else if (type === 'Withdraw' && !isBonus) {
      stats.withdrawTransactions++
      stats.totalWithdrawAmount += amount
      stats.totalWithdrawRegularAmount += amount
      cashWithdrawTotal += amount
      
      // ถอนโดยแอดมิน: withdrawBy=Manual/Manaul
      if (withdrawBy === 'Manaul' || withdrawBy === 'Manual' || withdrawBy === 'manual') {
        stats.adminWithdrawAmount += amount
      } 
      // ถอนอัตโนมัติ: withdrawBy=Auto
      else if (withdrawBy === 'Auto' || withdrawBy === 'auto') {
        stats.autoWithdrawAmount += amount
      }
    }
    // 5. ถอนโบนัส: type=Withdraw, isBonus=true
    else if (type === 'Withdraw' && isBonus) {
      stats.withdrawTransactions++
      stats.totalWithdrawAmount += amount
      stats.totalWithdrawBonusAmount += amount
    }
  })

  // คำนวณกำไรสุทธิ ตาม Angular: cashDeposit - (cashWithdraw + bonusDeposit + returnToMember)
  stats.netProfit = cashDepositTotal - (cashWithdrawTotal + bonusDepositTotal + returnToMemberTotal)

  console.log('📊 [calculateFinancialStats] Result:', {
    cashDeposit: cashDepositTotal,
    cashWithdraw: cashWithdrawTotal,
    bonusDeposit: bonusDepositTotal,
    returnToMember: returnToMemberTotal,
    netProfit: stats.netProfit,
  })

  return stats
}

// ============================================
// Dashboard Service
// ============================================

export const DashboardService = {
  /**
   * ดึงข้อมูล Financial Statistics จาก DepositWithdraw
   * อ้างอิงจาก Angular: loadFinancialStatistics()
   */
  async getFinancialStats(
    sessionToken: string,
    params: DashboardQueryParams
  ): Promise<FinancialStats> {
    console.log('📊 [DashboardService] getFinancialStats:', {
      startDate: params.startDate?.toISOString(),
      endDate: params.endDate?.toISOString(),
    })
    
    try {
      // ลอง query แบบง่ายก่อน - ไม่มี filter เพื่อ debug
      console.log('🔍 [DashboardService] Testing basic query first...')
      
      const testResult = await queryClass('DepositWithdraw', {
        limit: 5,
        order: '-createdAt',
      }, sessionToken)
      
      console.log('🔍 [DashboardService] Basic query found:', testResult.results?.length, 'records')
      if (testResult.results?.length > 0) {
        const sample = testResult.results[0]
        console.log('📋 [DashboardService] Sample record:', {
          objectId: sample.objectId,
          type: sample.type,
          amount: sample.amount,
          status: sample.status,
          commission: sample.commission,
          isBonus: sample.isBonus,
          createdAt: sample.createdAt,
        })
      }
      
      // Query DepositWithdraw - รองรับทั้ง V1 (Approved) และ V2 (Completed)
      // ✅ V2: ใช้ Completed สำหรับ transaction ที่สำเร็จแล้ว
      const where: Record<string, any> = {
        status: { $in: ['Approved', 'Completed'] }, // ✅ รองรับทั้ง V1 และ V2
        deletedAt: { $exists: false },
      }
      
      // Date filter - ใช้ createdAt ตาม Angular
      if (params.startDate || params.endDate) {
        where.createdAt = {}
        if (params.startDate) {
          where.createdAt.$gte = { __type: 'Date', iso: params.startDate.toISOString() }
        }
        if (params.endDate) {
          where.createdAt.$lte = { __type: 'Date', iso: params.endDate.toISOString() }
        }
      }
      
      console.log('📡 [DashboardService] Financial Query where:', JSON.stringify(where))
      
      const result = await queryClass('DepositWithdraw', {
        where,
        limit: 10000,
        order: '-createdAt',
      }, sessionToken)
      
      console.log('📊 [DashboardService] Found', result.results?.length, 'transactions')
      
      // ถ้าไม่พบข้อมูล ลอง query โดยไม่มี date filter เพื่อ debug
      if (!result.results || result.results.length === 0) {
        console.warn('⚠️ [DashboardService] No transactions found with date filter, trying without...')
        
        const debugResult = await queryClass('DepositWithdraw', {
          where: {
            status: { $in: ['Approved', 'Completed'] }, // ✅ รองรับทั้ง V1 และ V2
            deletedAt: { $exists: false },
          },
          limit: 5,
          order: '-createdAt',
        }, sessionToken)
        
        console.log('🔍 [DashboardService] Debug query found:', debugResult.results?.length, 'records')
        if (debugResult.results?.length > 0) {
          console.log('📋 [DashboardService] Latest record date:', debugResult.results[0].createdAt)
          console.log('📋 [DashboardService] Sample:', {
            type: debugResult.results[0].type,
            amount: debugResult.results[0].amount,
            status: debugResult.results[0].status,
            commission: debugResult.results[0].commission,
          })
        }
      }
      
      // Log sample data for debugging
      if (result.results?.length > 0) {
        console.log('📋 [DashboardService] Sample transaction:', {
          type: result.results[0].type,
          amount: result.results[0].amount,
          status: result.results[0].status,
          isBonus: result.results[0].isBonus,
          create_from: result.results[0].create_from,
          commission: result.results[0].commission,
        })
      }
      
      return calculateFinancialStats(result.results || [])
    } catch (error) {
      console.error('❌ [DashboardService] getFinancialStats error:', error)
      // Return empty stats on error
      return {
        totalDepositAmount: 0,
        totalDepositRegularAmount: 0,
        totalDepositBonusAmount: 0,
        totalWithdrawAmount: 0,
        totalWithdrawRegularAmount: 0,
        totalWithdrawBonusAmount: 0,
        totalReturnToMemberAmount: 0,
        netProfit: 0,
        adminDepositAmount: 0,
        autoDepositAmount: 0,
        adminWithdrawAmount: 0,
        autoWithdrawAmount: 0,
        totalTransactions: 0,
        depositTransactions: 0,
        withdrawTransactions: 0,
        pendingTransactions: 0,
        approvedTransactions: 0,
      }
    }
  },
  
  /**
   * ดึงข้อมูล Member Statistics จาก _User
   * อ้างอิงจาก Angular: loadUserStatistics()
   */
  async getMemberStats(
    sessionToken: string,
    params: DashboardQueryParams
  ): Promise<MemberStats> {
    console.log('📊 [DashboardService] getMemberStats:', {
      startDate: params.startDate?.toISOString(),
      endDate: params.endDate?.toISOString(),
    })
    
    try {
      // 1. Total Members (ไม่รวม admin) - simplified query
      let totalMembers = 0
      try {
        const totalResult = await queryClass('_User', {
          where: {
            deletedAt: { $exists: false },
          },
          count: 1,
          limit: 0,
        }, sessionToken)
        totalMembers = totalResult.count || 0
      } catch (e) {
        console.warn('Could not count total members:', e)
      }
      
      // 2. New Members ในช่วงวันที่
      let totalNewMembers = 0
      try {
        const newMembersWhere: Record<string, any> = {
          deletedAt: { $exists: false },
        }
        
        if (params.startDate || params.endDate) {
          newMembersWhere.createdAt = {}
          if (params.startDate) {
            newMembersWhere.createdAt.$gte = { __type: 'Date', iso: params.startDate.toISOString() }
          }
          if (params.endDate) {
            newMembersWhere.createdAt.$lte = { __type: 'Date', iso: params.endDate.toISOString() }
          }
        }
        
        const newMembersResult = await queryClass('_User', {
          where: newMembersWhere,
          count: 1,
          limit: 0,
        }, sessionToken)
        totalNewMembers = newMembersResult.count || 0
      } catch (e) {
        console.warn('Could not count new members:', e)
      }
      
      // 3 & 4. Active Members และ New Members with Deposit
      // คำนวณจาก DepositWithdraw ที่มี Approved ในช่วงเวลา
      let activeMembers = 0
      let newMembersWithDeposit = 0
      try {
        const txWhere: Record<string, any> = {
          status: { $in: ['Approved', 'Completed'] }, // ✅ รองรับทั้ง V1 และ V2
          deletedAt: { $exists: false },
        }
        
        if (params.startDate || params.endDate) {
          txWhere.createdAt = {}
          if (params.startDate) {
            txWhere.createdAt.$gte = { __type: 'Date', iso: params.startDate.toISOString() }
          }
          if (params.endDate) {
            txWhere.createdAt.$lte = { __type: 'Date', iso: params.endDate.toISOString() }
          }
        }
        
        const txResult = await queryClass('DepositWithdraw', {
          where: txWhere,
          limit: 5000,
          keys: ['member_id', 'type'],
        }, sessionToken)
        
        // Count unique members with approved transactions
        const uniqueActive = new Set<string>()
        const uniqueDepositors = new Set<string>()
        
        ;(txResult.results || []).forEach((t: any) => {
          const memberId = t.member_id?.objectId
          if (memberId) {
            uniqueActive.add(memberId)
            if (t.type === 'Deposit') {
              uniqueDepositors.add(memberId)
            }
          }
        })
        
        activeMembers = uniqueActive.size
        // Simplified: count depositors as "new members with deposit" for now
        newMembersWithDeposit = Math.min(uniqueDepositors.size, totalNewMembers)
      } catch (e) {
        console.warn('Could not count active members:', e)
      }
      
      console.log('📊 [DashboardService] Member stats:', {
        total: totalMembers,
        new: totalNewMembers,
        withDeposit: newMembersWithDeposit,
        active: activeMembers,
      })
      
      return {
        totalMembers,
        totalNewMembers,
        newMembersWithDeposit,
        activeMembers,
      }
    } catch (error) {
      console.error('❌ [DashboardService] getMemberStats error:', error)
      return {
        totalMembers: 0,
        totalNewMembers: 0,
        newMembersWithDeposit: 0,
        activeMembers: 0,
      }
    }
  },
  
  /**
   * ดึงข้อมูล Daily Table (Mock - ยังไม่ implement)
   */
  async getDailyTable(
    _sessionToken: string,
    _params: DashboardQueryParams
  ): Promise<DailyTableData[]> {
    console.log('📊 [DashboardService] getDailyTable - Not implemented yet')
    return []
  },
  
  /**
   * ดึงข้อมูล Chart (Mock - ยังไม่ implement)
   */
  async getChartData(
    _sessionToken: string,
    _params: DashboardQueryParams
  ): Promise<ChartData> {
    console.log('📊 [DashboardService] getChartData - Not implemented yet')
    return { categories: [], series: [] }
  },
  
  /**
   * ดึงข้อมูล Dashboard ทั้งหมด (รวม)
   */
  async getDashboardData(
    sessionToken: string,
    params: DashboardQueryParams
  ): Promise<DashboardData> {
    console.log('📊 [DashboardService] getDashboardData:', params)
    
    // Load all data in parallel
    const [financial, members, dailyTable, chart] = await Promise.all([
      this.getFinancialStats(sessionToken, params),
      this.getMemberStats(sessionToken, params),
      this.getDailyTable(sessionToken, params),
      this.getChartData(sessionToken, params),
    ])
    
    return {
      financial,
      members,
      dailyTable,
      chart,
    }
  },
  
  /**
   * Helper: Get date range by filter type
   */
  getDateRange,
}

export default DashboardService

